import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpdateMerchantSettings } from '@/api/domains/merchant-settings';
import { toApiProblem, toastProblem } from '@/lib/errors';

import { SettingsToggleRow } from '../components/SettingsSection';

interface AddressReplaceSectionProps {
  enabled: boolean;
}

/**
 * Replaces the Shopify order's shipping address with the selected pickup
 * point's address (the buyer's original address stays preserved on the
 * order). Saves immediately on click.
 */
export function AddressReplaceSection({ enabled }: AddressReplaceSectionProps) {
  const { t } = useTranslation('settings');

  const update = useUpdateMerchantSettings();

  const [pending, setPending] = useState<boolean | null>(null);
  const checked = pending ?? enabled;

  useEffect(() => {
    if (pending !== null && pending === enabled) {
      setPending(null);
    }
  }, [pending, enabled]);

  const handleToggle = (next: boolean) => {
    if (update.isPending) return;
    setPending(next);
    update.mutate(
      { settings: { point_address_replace_enabled: next } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => {
          setPending(null);
          toastProblem(toApiProblem(error));
        },
      },
    );
  };

  return (
    <SettingsToggleRow
      label={t('checkout.addressReplace.label')}
      description={t('checkout.addressReplace.description')}
      checked={checked}
      onCheckedChange={handleToggle}
    />
  );
}
